import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
    ApiService,
    Mk2Request,
    Mk2Response,
    Pagination
} from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { FeatureFlagService } from '@lsq-frontend/common-ui';
import { EnumType, jsonToGraphQLQuery } from 'json-to-graphql-query';
import { startWith } from 'rxjs/operators';
import {
    SortDirection,
    TransferDetailInput
} from '../../../../financing-graphql/src/lib/model/models';
import { IExportRequest } from '../models/export-request';
import { ExportType } from '../models/export-type';
import { mapTranferDetailsSortColumn } from '@lsq-frontend/remote-services/financing-graphql';

export class BuyerPaymentsDetailExportRequest extends Mk2Request {
    batchKey?: string;
    buyerPaymentsListPaymentTypeEnum?:
        | 'EARLY_PAYMENT'
        | 'EARLY_PAY_REMITTANCE'
        | 'INVOICE_TERM'
        | 'UNKNOWN_PAYMENT_TYPE'
        | 'PAY_LATER_PAYMENT';
    pagination?: Pagination;
    searchValue?: string;
    supplierSearchList?: string[];

    constructor(initial?: Partial<BuyerPaymentsDetailExportRequest>) {
        super();
        if (initial != null) {
            Object.keys(initial).forEach((key) => {
                this[key] = initial[key];
            });
        }
    }
}

export class BuyerPaymentsDetailExportResponse extends Mk2Response {
    fileString?: string;
    pagination?: Pagination;
    searchValue?: string;

    constructor(initial?: Partial<BuyerPaymentsDetailExportResponse>) {
        super();
        if (initial != null) {
            Object.keys(initial).forEach((key) => {
                this[key] = initial[key];
            });
        }
    }
}

@Injectable({ providedIn: 'root' })
export class GetBuyerPaymentDetailsExportService extends ApiService<
    BuyerPaymentsDetailExportRequest,
    BuyerPaymentsDetailExportResponse
> {
    public requestType = BuyerPaymentsDetailExportRequest;
    public responseType = BuyerPaymentsDetailExportResponse;
    public endpointName = 'buyer/BuyerPaymentsList/GetPaymentExport';
    public serviceName = 'financing';

    private _isPayLaterEnabled = false;

    constructor(
        http: HttpClient,
        @Inject(SETTINGS) settings: ISettings,
        private _featureFlagService: FeatureFlagService
    ) {
        super(http, settings);

        this._featureFlagService
            .isOn(FeatureFlags.PayLater)
            .pipe(startWith(this._featureFlagService.isOnSync(FeatureFlags.PayLater)))
            .subscribe((isOn) => {
                this._isPayLaterEnabled = isOn;
                if (isOn) {
                    this.endpointName = 'v2/financingExport';
                    this.serviceName = 'reportCenter';
                    this.usePascalForUrl = false;
                } else {
                    this.endpointName = 'buyer/BuyerPaymentsList/GetPaymentExport';
                    this.serviceName = 'financing';
                    this.usePascalForUrl = true;
                }
            });
    }

    public requestMapper(
        req: BuyerPaymentsDetailExportRequest
    ): IExportRequest | BuyerPaymentsDetailExportRequest {
        if (this._isPayLaterEnabled) {
            const { skip, take, sortColumn, sortDirection } = req.pagination;
            const input: TransferDetailInput = {
                transferId: req.batchKey,
                companyFilter: {
                    supplierIds: []
                },
                invoiceNumberSearch: req.searchValue ?? '',
                suppliers: req.supplierSearchList ?? [],
                pagination: {
                    skip: skip || 0,
                    take: take || 0,
                    sortColumn: new EnumType(mapTranferDetailsSortColumn(sortColumn)),
                    sortDirection: new EnumType(
                        SortDirection[sortDirection] ?? SortDirection.DESCENDING
                    )
                }
            };
            const query = jsonToGraphQLQuery({
                transferDetail: {
                    __args: { input },
                    rows: {
                        statusLegacy: true,
                        isGroup: true,
                        supplierKey: true,
                        supplierId: true,
                        supplier: true,
                        recipient: true,
                        invoiceNumber: true,
                        invoiceDate: true,
                        invoiceAmount: {
                            cents: true,
                            currencyCode: true
                        },
                        paymentDate: true,
                        paymentAmount: {
                            cents: true,
                            currencyCode: true
                        },
                        payLaterAdjustedDueDate: true,
                        originalDueDate: true,
                        groupId: true,
                        earlyPaymentDate: true,
                        transferType: true,
                        onTermsPaymentDate: true,
                        buyerFundingChargeTotal: {
                            cents: true,
                            currencyCode: true
                        },
                        buyerFundingChargePaidTotal: {
                            cents: true,
                            currencyCode: true
                        },
                        numberOfDaysPaidEarly: true,
                        creditMemos: {
                            id: true,
                            date: true,
                            creditMemoNumber: true,
                            allocatedAmount: {
                                cents: true,
                                currencyCode: true
                            }
                        },
                        approvals: {
                            id: true,
                            originalDueDate: true,
                            amount: {
                                cents: true,
                                currencyCode: true
                            },
                            adjustedAmount: {
                                cents: true,
                                currencyCode: true
                            },
                            invoice: {
                                date: true,
                                invoiceNumber: true,
                                amount: {
                                    cents: true,
                                    currencyCode: true
                                }
                            },
                            creditMemoAllocations: {
                                id: true,
                                approvalId: true,
                                allocatedAmount: {
                                    cents: true,
                                    currencyCode: true
                                },
                                creditMemoId: true,
                                creditMemo: {
                                    creditMemoNumber: true,
                                    date: true
                                },
                                allocationType: true
                            }
                        }
                    },
                    transferSummary: {
                        transferType: true,
                        buyerAmountPaidToLsq: {
                            cents: true,
                            currencyCode: true
                        },
                        buyerAmountPaidToSupplier: {
                            cents: true,
                            currencyCode: true
                        },
                        recipientType: true
                    }
                }
            });
            return {
                fileType: 'csv',
                exportType: ExportType.BUYER_PAYMENT,
                query: `{${query}}`
            };
        }
        return req;
    }

    public get endpointUrl(): string {
        return this._isPayLaterEnabled ? super.endpointUrl.replace('/api', '') : super.endpointUrl;
    }
}
